import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { BadgeCheck, Building2, ChevronDown, FileText, Gauge, Hospital, Package, Printer, XCircle } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { KpiCard, KpiGrid } from "../components/KpiCard";
import { cn } from "@/shared/lib/cn";
import { fetchScopeCompanies, type ScopeCompany } from "../api/insurerApi";
import { fetchClaimsReport, fetchInsurerPackages, type ClaimsReport, type ReportStats } from "../api/claimsApi";
import { getWorkContext } from "../workContext";
import { openHtmlReport } from "../api/invoicePrintApi";
import { getApiBaseUrl } from "@/shared/lib/apiClient";

function money(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function percent(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %`;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function Section({ title, children, className, action }: { title: string; children: ReactNode; className?: string; action?: ReactNode }) {
  return (
    <section className={cn("theme-card break-inside-avoid p-5", className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {action ? <div className="print:hidden">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

type Column<T> = { label: string; render: (row: T) => ReactNode; numeric?: boolean };

/** Lignes affichées avant « Voir + ». */
const VISIBLE_ROWS = 10;

/** Couleur de la jauge selon la part, comme les blocs du tableau de bord. */
function shareTone(share: number): string {
  if (share >= 40) return "bg-red-500 dark:bg-red-400";
  if (share >= 15) return "bg-amber-500 dark:bg-amber-400";
  return "bg-emerald-500 dark:bg-emerald-400";
}

function ShareGauge({ share }: { share: number }) {
  return (
    <span className="inline-flex items-center justify-end gap-2">
      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-card-muted" aria-hidden="true">
        <span className={cn("block h-full rounded-full", shareTone(share))} style={{ width: `${Math.min(100, Math.max(2, share))}%` }} />
      </span>
      <span className="w-14 text-right">{percent(share)}</span>
    </span>
  );
}

function Table<T>({ rows, columns, empty }: { rows: T[]; columns: Column<T>[]; empty: string }) {
  const [expanded, setExpanded] = useState(false);
  if (rows.length === 0) return <p className="text-sm text-muted">{empty}</p>;
  const visible = expanded ? rows : rows.slice(0, VISIBLE_ROWS);
  return (
    <>
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-card-muted text-left text-xs uppercase text-muted">
          <tr>
            <th className="px-3 py-2">#</th>
            {columns.map((column) => (
              <th key={column.label} className={`px-3 py-2 ${column.numeric ? "text-right" : ""}`}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row, index) => (
            <tr key={index} className="border-t border-border">
              <td className="px-3 py-2 tabular-nums text-muted">{index + 1}</td>
              {columns.map((column) => (
                <td key={column.label} className={`px-3 py-2 text-foreground ${column.numeric ? "text-right tabular-nums" : ""}`}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {rows.length > VISIBLE_ROWS ? (
      <button type="button" onClick={() => setExpanded(!expanded)} className="mt-2 text-sm font-medium text-primary hover:underline print:hidden">
        {expanded ? "Voir moins" : `Voir + (${rows.length - VISIBLE_ROWS})`}
      </button>
    ) : null}
    </>
  );
}

const statColumns = <T extends ReportStats>(): Column<T>[] => [
  { label: "Nbr d'actes", render: (r) => r.acts, numeric: true },
  { label: "Conso totale", render: (r) => money(r.validated), numeric: true },
  { label: "Conso moyenne", render: (r) => money(r.average), numeric: true },
  { label: "Part", render: (r) => <ShareGauge share={r.share} />, numeric: true },
];

/**
 * Compte Assurance : rapport de sinistralité sur une période, pour tout le portefeuille ou une entreprise.
 */
const ribbonLabel = "mb-1.5 block text-xs font-medium text-muted-foreground";
const ribbonField =
  "h-11 w-full rounded-xl border border-border bg-card px-3 text-sm font-medium text-foreground outline-none transition-[background-color,border-color,box-shadow] duration-150 hover:border-primary/30 focus:border-accent focus:ring-4 focus:ring-accent/25 dark:[color-scheme:dark]";

/** Seuils du filtre « Plafond » : part du plafond par personne consommée par les bénéficiaires d'un package. */
const CEILING_OPTIONS = [
  { value: "", label: "Tous" },
  { value: "50", label: "≥ 50 % consommé" },
  { value: "80", label: "≥ 80 % consommé" },
  { value: "100", label: "Plafond dépassé" },
] as const;

export function InsurerReportPage() {
  const [from, setFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo] = useState(today());
  const [companies, setCompanies] = useState<ScopeCompany[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [report, setReport] = useState<ClaimsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Filtres appliqués dans la page aux tableaux détaillés (l'API ne filtre que par période et entreprise).
  const [packageFilter, setPackageFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [ceilingFilter, setCeilingFilter] = useState("");
  const [packageCeilings, setPackageCeilings] = useState<Record<string, number | null>>({});

  useEffect(() => {
    fetchScopeCompanies().then(setCompanies).catch(() => setCompanies([]));
    fetchInsurerPackages()
      .then((packages) => setPackageCeilings(Object.fromEntries(packages.map((pkg) => [pkg.name, pkg.person_ceiling]))))
      .catch(() => setPackageCeilings({}));
    setCompanyId(getWorkContext().companyId);
  }, []);

  const load = useCallback(async (companyOverride?: number | null) => {
    const id = companyOverride === undefined ? companyId : companyOverride;
    setLoading(true);
    setError(null);
    try {
      setReport(await fetchClaimsReport(from, to, id ?? undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rapport indisponible.");
    } finally {
      setLoading(false);
    }
  }, [from, to, companyId]);

  // Le rapport se recalcule dès qu'une date ou l'entreprise change (petit délai pendant la saisie d'une date).
  useEffect(() => {
    const timer = setTimeout(() => void load(), 400);
    return () => clearTimeout(timer);
  }, [load]);

  const key = report?.key_figures;

  /** Part du plafond consommée : validé / (plafond par personne × bénéficiaires), en %. */
  const ceilingRate = useCallback(
    (row: { label: string; validated: number; beneficiaries: number }) => {
      const ceiling = packageCeilings[row.label];
      return ceiling && row.beneficiaries > 0 ? (row.validated / (ceiling * row.beneficiaries)) * 100 : null;
    },
    [packageCeilings],
  );

  const filtered = useMemo(() => {
    if (!report) return null;
    const threshold = ceilingFilter ? Number(ceilingFilter) : null;
    const byPackage = report.by_package.filter((row) => {
      if (packageFilter && row.label !== packageFilter) return false;
      if (threshold !== null) {
        const rate = ceilingRate(row);
        return rate !== null && (threshold === 100 ? rate > 100 : rate >= threshold);
      }
      return true;
    });
    const byProvider = providerFilter ? report.by_provider.filter((row) => row.label === providerFilter) : report.by_provider;
    const byNature = providerFilter
      ? report.by_nature
          .map((row) => ({ ...row, providers: row.providers.filter((p) => p.label === providerFilter) }))
          .filter((row) => row.providers.length > 0)
      : report.by_nature;
    return { byPackage, byProvider, byNature };
  }, [report, packageFilter, providerFilter, ceilingFilter, ceilingRate]);

  const detailFiltersActive = Boolean(packageFilter || providerFilter || ceilingFilter);

  // Packages proposés : tous ceux de l'assureur, puis ceux des dépenses absents de la liste (« Sans package »…).
  const packageOptions = useMemo(() => {
    const names = Object.keys(packageCeilings).sort((a, b) => a.localeCompare(b, "fr"));
    for (const row of report?.by_package ?? []) {
      if (!names.includes(row.label)) names.push(row.label);
    }
    return names;
  }, [packageCeilings, report]);

  // Après un rechargement, un package ou un prestataire absent de la nouvelle période ne reste pas sélectionné.
  useEffect(() => {
    if (!report) return;
    if (packageFilter && !packageOptions.includes(packageFilter)) setPackageFilter("");
    if (providerFilter && !report.by_provider.some((row) => row.label === providerFilter)) setProviderFilter("");
  }, [report, packageFilter, providerFilter, packageOptions]);

  return (
    <div className="report-compact">
      <Breadcrumbs items={[{ label: "Rapport de sinistralité" }]} />

      {/* Réglages du rapport : bandeau vert profond, comme le header. */}
      <section className="header-ribbon relative isolate mb-5 rounded-[var(--radius-card)] p-4 sm:p-5 print:hidden">
        <div className="flex flex-col gap-4">
          {/* 1re ligne : filtres. */}
          <div className="flex flex-wrap items-end gap-3 2xl:flex-nowrap">
          <div className="w-full sm:w-auto sm:w-[9.5rem] sm:shrink-0">
            <label htmlFor="report-from" className={ribbonLabel}>Du</label>
            <span className="relative block">
              <input id="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={cn(ribbonField, "cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-80")} />
            </span>
          </div>
          <div className="w-full sm:w-auto sm:w-[9.5rem] sm:shrink-0">
            <label htmlFor="report-to" className={ribbonLabel}>Au</label>
            <span className="relative block">
              <input id="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={cn(ribbonField, "cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-80")} />
            </span>
          </div>
          <div className="min-w-[12rem] flex-1 2xl:min-w-0">
            <label htmlFor="report-company" className={ribbonLabel}>Entreprise</label>
            <span className="relative block">
              <span className="pointer-events-none absolute left-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg bg-accent text-accent-foreground" aria-hidden="true">
                <Building2 size={15} strokeWidth={2} />
              </span>
              <select
                id="report-company"
                value={companyId ?? ""}
                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
                className={cn(ribbonField, "cursor-pointer appearance-none truncate pl-11 pr-9")}
              >
                <option value="">Portefeuille</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true" />
            </span>
          </div>
          {(
            [
              {
                id: "report-package",
                label: "Package",
                icon: Package,
                value: packageFilter,
                onChange: setPackageFilter,
                options: [{ value: "", label: "Tous" }, ...packageOptions.map((name) => ({ value: name, label: name }))],
              },
              {
                id: "report-provider",
                label: "Prestataire",
                icon: Hospital,
                value: providerFilter,
                onChange: setProviderFilter,
                options: [{ value: "", label: "Tous" }, ...(report?.by_provider ?? []).map((row) => ({ value: row.label, label: row.label }))],
              },
              {
                id: "report-ceiling",
                label: "Plafond",
                icon: Gauge,
                value: ceilingFilter,
                onChange: setCeilingFilter,
                options: CEILING_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
              },
            ] as const
          ).map((filter) => (
            <div key={filter.id} className="min-w-[12rem] flex-1 2xl:min-w-0">
              <label htmlFor={filter.id} className={ribbonLabel}>{filter.label}</label>
              <span className="relative block">
                <span className="pointer-events-none absolute left-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg bg-accent text-accent-foreground" aria-hidden="true">
                  <filter.icon size={15} strokeWidth={2} />
                </span>
                <select
                  id={filter.id}
                  value={filter.value}
                  disabled={!report}
                  onChange={(e) => filter.onChange(e.target.value)}
                  className={cn(ribbonField, "cursor-pointer appearance-none truncate pl-11 pr-9 disabled:cursor-not-allowed disabled:opacity-60")}
                >
                  {filter.options.map((option) => (
                    <option key={option.value || "all"} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true" />
              </span>
            </div>
          ))}
            <button
              type="button"
              disabled={!report}
              onClick={() =>
                // Rapport de gestion santé (A4) : statistiques, mémo de règlement, fiche de réfaction.
                // Mêmes filtres que la page (package, prestataire, plafond) : tout le rapport porte sur la sélection.
                void openHtmlReport(
                  `${getApiBaseUrl()}/insurer/report/print?${new URLSearchParams({
                    from,
                    to,
                    ...(companyId ? { company_id: String(companyId) } : {}),
                    ...(packageFilter ? { package: packageFilter } : {}),
                    ...(providerFilter ? { provider: providerFilter } : {}),
                    ...(ceilingFilter ? { ceiling: ceilingFilter } : {}),
                  }).toString()}`,
                  "Rapport imprimable indisponible.",
                ).catch((err) => setError(err instanceof Error ? err.message : "Rapport imprimable indisponible."))
              }
              className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-medium text-foreground transition-colors duration-150 hover:border-accent hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60"
            >
              <Printer size={15} aria-hidden="true" />
              Imprimer / PDF
            </button>
          </div>

        </div>
      </section>

      {detailFiltersActive ? (
        <p className="-mt-2 mb-5 text-xs text-muted-foreground print:hidden">
          Package, prestataire et plafond filtrent les tableaux détaillés. Les données clés couvrent toute la période et le périmètre choisis.
        </p>
      ) : null}

      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}
      {loading && !report ? <p className="text-sm text-muted">Calcul du rapport…</p> : null}

      {report && key ? (
        <div className="flex flex-col gap-5">
          <Section
            title="Données clés"
            className="header-ribbon relative isolate"
            action={(
              <select
                id="key-figures-company"
                value={companyId ?? ""}
                onChange={(event) => {
                  const next = event.target.value ? Number(event.target.value) : null;
                  setCompanyId(next);
                }}
                aria-label="Filtrer par entreprise"
                className="h-8 max-w-[11rem] rounded-lg border border-border bg-card px-2 text-xs text-foreground"
              >
                <option value="">Tous</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            )}
          >
            <p className="mb-3 text-xs text-muted">
              Extraction du {key.extraction_date} · période du {key.period.from} au {key.period.to} ·{" "}
              {key.companies.length === 1 ? key.companies[0] : `${key.companies.length} entreprises`}
            </p>
            <KpiGrid className="grid-cols-1 sm:grid-cols-3">
              <KpiCard tone="slate" icon={FileText} href="/dashboard/invoices" label="Total demandé" value={money(key.requested_total)} />
              <KpiCard tone="mint" icon={BadgeCheck} href="/dashboard/invoices/approved" label="Total validé" value={money(key.validated_total)} />
              <KpiCard tone="red" icon={XCircle} label="Taux de rejet" value={percent(key.rejection_rate)} />
            </KpiGrid>
            {key.unassigned_lines > 0 ? (
              <p className="mt-3 text-xs text-amber-600">
                {key.unassigned_lines} ligne(s) d'acte sans bénéficiaire : elles comptent dans les totaux mais pas dans les sections par statut, package et famille.
              </p>
            ) : null}
          </Section>

          <Section title="Dépenses par catégorie d'assuré">
            <Table
              rows={report.by_status}
              empty="Aucune dépense sur la période."
              columns={[
                { label: "Catégorie", render: (r) => r.label },
                { label: "Total demandé", render: (r) => money(r.requested), numeric: true },
                { label: "Total validé", render: (r) => money(r.validated), numeric: true },
                { label: "Part", render: (r) => <ShareGauge share={r.share} />, numeric: true },
              ]}
            />
          </Section>

          <Section title="Dépenses par package">
            <Table
              rows={filtered?.byPackage ?? []}
              empty={detailFiltersActive ? "Aucun package ne correspond aux filtres." : "Aucune dépense sur la période."}
              columns={[
                { label: "Package", render: (r) => r.label },
                { label: "Bénéficiaires", render: (r) => r.beneficiaries, numeric: true },
                ...statColumns(),
              ]}
            />
          </Section>

          <Section title="Dépenses par nature d'acte">
            <Table
              rows={filtered?.byNature ?? []}
              empty={providerFilter ? "Aucune dépense de ce prestataire sur la période." : "Aucune dépense sur la période."}
              columns={[
                { label: "Nature", render: (r) => r.label },
                ...statColumns(),
              ]}
            />
          </Section>

          <Section title="Dépenses par prestataire">
            <Table
              rows={filtered?.byProvider ?? []}
              empty={providerFilter ? "Aucune dépense de ce prestataire sur la période." : "Aucune dépense sur la période."}
              columns={[{ label: "Prestataire", render: (r) => r.label }, { label: "Bénéficiaires", render: (r) => r.beneficiaries, numeric: true }, ...statColumns()]}
            />
          </Section>

          <Section title="Dépenses par affection">
            <Table
              rows={report.by_pathology}
              empty="Aucune dépense sur la période."
              columns={[{ label: "Affection", render: (r) => r.label }, { label: "Cas", render: (r) => r.cases, numeric: true }, ...statColumns()]}
            />
          </Section>

          <Section title="Dépenses par famille">
            <Table
              rows={report.by_family}
              empty="Aucune ligne rattachée à un assuré sur la période."
              columns={[{ label: "Nom de l'assuré", render: (r) => r.name }, { label: "Dépendants", render: (r) => r.dependants, numeric: true }, ...statColumns()]}
            />
          </Section>

          <p className="text-xs text-muted">
            Montant validé : retenu par l'Audit ligne par ligne, sinon au prorata du montant validé de la facture.
            Part : poste / total validé.
          </p>
        </div>
      ) : null}
    </div>
  );
}
